/* eslint-disable no-underscore-dangle */
import moment from 'moment'
import { find } from 'lodash'
import { OUTPUTS, FORMATS } from '@/entities/fields'

export const basicFieldConfig = {
    suppressMovable: true,
    suppressNavigable: false,
    resizable: true,
    filter: 'agTextColumnFilter',
    // TODO: which columns have the prop
    columnGroupShow: 'open',
    cellStyle: getCellStyle,
    valueFormatter: getValueFormatter,
    // Se anula el botón "Enter" como editar celda pero se queda como guardado de cambios de la celda.
    suppressKeyboardEvent: params => (!params.editing && params.event.key === 'Enter')
    ,
}

// Transform styles founded on meta info to CSS
function getStyle (style) {
    return {
        'bg-color': { 'background-color': style.color },
        'fg-color': { color: style.color },
        bold: { 'font-weight': 'bold' },
    }[style.type]
}

// For each field of each col set the styles founded on meta info
function getCellStyle ({ data, colDef }) {
    if (!data && !colDef) return {}
    const meta = [...(((data || {})._meta || {})[colDef.field] || [])]
    return meta.reduce((styles, style) => ({ ...styles, ...getStyle(style) }), {})
}

// For each field of each row check if there is a visual text ('text') on meta info
function getValueFormatter ({ data, value, colDef }) {
    const meta = [...(((data || {})._meta || {})[colDef.field] || [])]
    const replacementValue = find(meta, { type: 'text' })
    return replacementValue ? replacementValue.text : value
}

const DEFINITIONS = {
    [OUTPUTS.DEFAULT]: ({ field }) => ({ ...field, ...basicFieldConfig }),
    [OUTPUTS.NUMBER]: ({ field }) => ({
        ...basicFieldConfig,
        ...field,
        filter: 'agNumberColumnFilter',
    }),
    [OUTPUTS.DECIMAL]: ({ field }) => ({
        ...basicFieldConfig,
        ...field,
        filter: 'agNumberColumnFilter',
        cellRenderer: 'decimalCellRender',
    }),
    [OUTPUTS.BOOL]: ({ field }) => ({
        ...field,
        ...basicFieldConfig,
        filter: 'agSetColumnFilter',
        cellRenderer: 'boolCellRender',
        cellEditor: 'boolCellEditor',
    }),
    [OUTPUTS.DATE]: ({ field, options }) => ({
        ...basicFieldConfig,
        ...field,
        // Editar de fechas que funciona con el teclado.
        cellEditor: DatePicker,
        valueFormatter: ({ value }) => (![null, undefined].includes(value) ? moment(value).format(options.formatDate || FORMATS[OUTPUTS.DATE]) : ''),
        // cellEditor: 'dateCellEditor',
        filter: 'agDateColumnFilter',
        filterParams: {
            comparator (filterLocalDateAtMidnight, cellValue) {
                const momentCellValue = moment(cellValue)
                const momentFilterDate = moment(filterLocalDateAtMidnight)
                const differenceBetweenDates = momentCellValue.diff(momentFilterDate, 'days')
                if (differenceBetweenDates < 0) {
                    return -1
                }
                if (differenceBetweenDates > 0) {
                    return 1
                }
                return 0
            },
        },
    }),
    [OUTPUTS.SELECT]: ({ field, selectGroups = {} }) => ({
        ...basicFieldConfig,
        ...field,
        filter: 'agSetColumnFilter',
        // Editar propio del componente.
        // cellEditor: 'agRichSelectCellEditor',
        // Opciones de selección.
        // cellEditorParams: { values: Object.values(selectGroups[field.selectGroup]) },
        popup: true,
        // Editor que no se puede utilizar con el teclado:
        ...(selectGroups[field.selectGroup] ? {
            cellEditor: 'selectCellEditor',
            cellEditorParams: { values: selectGroups[field.selectGroup] },
            cellOptions: selectGroups[field.selectGroup],
        } : {}),
        valueFormatter: ({ value }) => value,
        // DISABLED OLD ID APPROACH (now "key" is same as "value")
        // valueFormatter: ({ value, colDef }) => {
        //     const options = selectGroups[colDef.selectGroup]
        //     const selecteds = Array.isArray(value) && options ? value.map(key => options[key]) : (options || {})[value]
        //     return (!options || !selecteds) ? value : selecteds
        // },
    }),
}

export default {
    DEFAULT: params => DEFINITIONS[OUTPUTS.DEFAULT](params),
    [OUTPUTS.BOOL]: params => DEFINITIONS[OUTPUTS.BOOL](params),
    [OUTPUTS.DATE]: params => DEFINITIONS[OUTPUTS.DATE](params),
    [OUTPUTS.NUMBER]: params => DEFINITIONS[OUTPUTS.NUMBER](params),
    [OUTPUTS.DECIMAL]: params => DEFINITIONS[OUTPUTS.DECIMAL](params),
    [OUTPUTS.SELECT]: params => DEFINITIONS[OUTPUTS.SELECT](params),
    [OUTPUTS.FLAG]: params => ({
        cellRenderer: 'flagCellRenderer',
        ...DEFINITIONS[OUTPUTS.SELECT](params),
    }),
    [OUTPUTS.DATETIME]: ({ field, options }) => {
        const params = { field, options: { ...(options || {}), formatDate: FORMATS[OUTPUTS.DATETIME] } }
        return DEFINITIONS[OUTPUTS.DATE](params)
    },
    [OUTPUTS.LINK]: params => ({
        cellRenderer: 'linkCellRenderer',
        ...DEFINITIONS[OUTPUTS.DEFAULT](params),
    }),
    [OUTPUTS.EMAIL]: params => ({
        cellRenderer: 'emailCellRenderer',
        ...DEFINITIONS[OUTPUTS.DEFAULT](params),
    }),
}

// Editar de fechas que funciona con el teclado.
class DatePicker {
    // gets called once before the renderer is used
    init (params) {
        // create the cell        
        this.eInput = document.createElement('input');

        let value = params.value;
        if (value !== null) {
            // Cuando entra se le pone la fecha europea.
            const d = value.substr(0, 10).split('-');
            this.eInput.value = `${d[2]}/${d[1]}/${d[0]}`;
        }
        this.eInput.classList.add('ag-input')
        this.eInput.style.height = '100%'

        // https://jqueryui.com/datepicker/
        $(this.eInput).datepicker({
            dateFormat: 'dd/mm/yy',
            showOn: 'button',
            onSelect: () => {
                this.eInput.focus()
            },
        })
    }

    // gets called once when grid ready to insert the element
    getGui () {
        return this.eInput
    }

    // focus and select can be done after the gui is attached
    afterGuiAttached () {
        this.eInput.focus()
        this.eInput.select()
    }

    // returns the new value after editing
    getValue () {
        // Cuando sale tiene que tener la fecha americana.
        const d = this.eInput.value.split('/')
        return `${d[2]}-${d[1]}-${d[0]}`
    }

    // any cleanup we need to be done here
    destroy () {
        // but this example is simple, no cleanup, we could
        // even leave this method out as it's optional
    }

    // if true, then this editor will appear in a popup
    isPopup () {
        // and we could leave this method out also, false is the default
        return false
    }
}
